import Axios from 'axios'
import storage from '../utils/storage'
import { WEEWILDAPI_URL } from '../config'
import * as Sentry from '@sentry/react'
import { LoginResponse } from '../features/auth/type'
import { store } from '../redux/store'
import { auth } from './apiList'
import { SHOW_NOTIFICATION } from '../redux/slices/showNotificationSlice'
import { HIDE_LOADER } from '../redux/slices/LoaderSlice'
import { LOGIN, LOGOUT } from '../redux/slices/authSlice'

// Create an Axios instance with a base URL
export const axios = Axios.create({
    baseURL: WEEWILDAPI_URL,
})

// Request interceptor to attach the Authorization header
axios.interceptors.request.use(
    (config) => {
        const token = store.getState().persistedReducers.auth.token
        if (token) {
            config.headers.authorization = `Bearer ${token}`
        }
        config.headers.Accept = 'application/json'
        config.headers['tenant'] = 'root'
        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Response interceptor to handle successful responses and errors
let isRefreshing = false
let failedQueue: {
    resolve: (token: string) => void
    reject: (error: string) => void
}[] = []

const processQueue = (error: string, token: string): void => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error)
        } else {
            prom.resolve(token)
        }
    })

    failedQueue = []
}

axios.interceptors.response.use(
    async (response) => {
        return response
    },
    async (error) => {
        Sentry.captureException(error)
        const originalRequest = error.config
        if (
            error.response?.data?.statusCode === 401 &&
            !originalRequest._retry &&
            error.response.data.source ===
                'WeeWildOnesApi.Infrastructure.Auth.Jwt.ConfigureJwtBearerOptions+<>c'
        ) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject })
                })
                    .then((token) => {
                        originalRequest.headers['Authorization'] =
                            'Bearer ' + token
                        return axios(originalRequest)
                    })
                    .catch((err) => {
                        return Promise.reject(err)
                    })
            }

            originalRequest._retry = true
            isRefreshing = true
            const authData = store.getState().persistedReducers.auth
            if (authData.token && authData.refreshToken) {
                const { token, refreshToken } = authData
                return new Promise((resolve, reject) => {
                    axios
                        .post(
                            auth.REFRESH_TOKEN,
                            { token, refreshToken },
                            {
                                headers: {
                                    tenant: 'root',
                                },
                            }
                        )
                        .then((res: LoginResponse) => {
                            const newAuthTokens = res.data
                            axios.defaults.headers.common['Authorization'] =
                                'Bearer ' + newAuthTokens.token
                            originalRequest.headers['Authorization'] =
                                'Bearer ' + newAuthTokens.token
                            store.dispatch(
                                LOGIN({
                                    isUser: true,
                                    token: newAuthTokens.token,
                                    refreshToken: newAuthTokens.refreshToken,
                                })
                            )
                            processQueue('', newAuthTokens.token)
                            resolve(axios(originalRequest))
                        })
                        .catch((err) => {
                            processQueue(err, '')
                            reject(err)
                            store.dispatch(LOGOUT())
                            storage.clearLocalStorage()
                        })
                        .then(() => {
                            isRefreshing = false
                        })
                })
            }
        }
        console.log(error)
        if (error.config.url === '/tokens') {
            store.dispatch(
                SHOW_NOTIFICATION({
                    message: 'Invalid Credentials',
                    type: 'error',
                })
            )
            store.dispatch(HIDE_LOADER())
            return
        }
        if (error.response.config.url === 'v1/timeoff/search') {
            store.dispatch(HIDE_LOADER())
            return
        }
        if (error.config.url === '/tokens/refresh') {
            store.dispatch(LOGOUT())
            store.dispatch(HIDE_LOADER())
            return
        }
        const exception: string = error?.response?.data?.exception
        const message: any = error?.response?.data?.messages
        store.dispatch(
            SHOW_NOTIFICATION({
                message:
                    message?.length > 0
                        ? message
                        : exception || 'Something went wrong',
                type: 'error',
            })
        )
        store.dispatch(HIDE_LOADER())
        return Promise.reject(error)
    }
)

export default axios
